import { ThemeProvider, CssBaseline } from "@mui/material";
import { OrionSnackbar } from "@orion/OrionComponents/OrionSnackbar";
import * as Sentry from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { SnackbarProvider } from "notistack";
import { StrictMode } from "react";
import ReactDOM from "react-dom/client";

import { AuthProvider } from "./contexts/AuthProvider";
import { queryClient } from "./query";
import { AuthRouterProvider, router } from "./router";
import orionDesignSystemTheme from "./theme";
import { createBootLoader } from "./utils/createBootLoader";
import { captureDemoFragment, stripUrlFragment } from "./utils/demoLink";

import { uploadManager } from "@/features/AssetUpload/uploadManager";
import { captureBlastroLinkFragment } from "@/features/Blastro/installationLink";
import { exportTracker } from "@/features/Exports/exportTracker";

import "./styles.css";

// FIRST, before Sentry.init below: httpContextIntegration puts location.href on
// every event, sendDefaultPii is on and replaysOnErrorSampleRate is 1.0, so a
// demo password still on the URL at init time rides along with every error and
// replay. The /demo route reads back what this captured.
captureDemoFragment();
captureBlastroLinkFragment();

async function enableMocking() {
  // Any VITE_FF_*_MSW flag turns mocking on. vite.config.ts matches on the
  // same naming convention when deciding whether to ship the worker script,
  // so adding a new mock flag needs no edit in either place.
  const mswEnabled = Object.entries(import.meta.env).some(
    ([key, value]) =>
      key.startsWith("VITE_FF_") && key.endsWith("_MSW") && value === "true",
  );

  if (!mswEnabled) {
    // The worker script ships in every build, so a browser that once ran MSW
    // keeps it registered even here, where it never activates and instead
    // re-issues every request through passthrough() — which breaks
    // cross-origin calls like Sentry's. Unregistering doesn't uncontrol the
    // current page, so don't block render on it: the next load is clean.
    void navigator.serviceWorker
      ?.getRegistrations()
      .then((registrations) =>
        Promise.all(
          registrations
            .filter((r) =>
              [r.active, r.installing, r.waiting].some((sw) =>
                sw?.scriptURL.includes("mockServiceWorker.js"),
              ),
            )
            .map((r) => r.unregister()),
        ),
      )
      .catch(() => {});

    return;
  }

  try {
    // Enable MSW for API mocking
    const { worker } = await import("./mocks/browser");

    // Start the worker with quiet mode to reduce console noise
    await worker.start({
      onUnhandledRequest: "bypass", // Don't warn about unhandled requests
    });

    console.log("[MSW] Mocking enabled for configured feature flags");
  } catch (error) {
    // Log the error but don't prevent app from loading
    console.error("[MSW] Failed to initialize Mock Service Worker:", error);
    console.warn(
      "[MSW] Continuing without mocks - real API will be used instead",
    );
  }
}

function createAppReadySignal() {
  let _resolve: () => void;
  const promise = new Promise<void>((resolve) => (_resolve = resolve));

  return { appReady: promise, markAppReady: () => _resolve() };
}

uploadManager.init(queryClient);
exportTracker.init(queryClient);

/** Drops any `#fragment` from the URL a Sentry event carries. */
function withoutUrlFragment<E extends { request?: { url?: string } }>(
  event: E,
): E {
  if (event.request?.url) {
    event.request.url = stripUrlFragment(event.request.url);
  }
  return event;
}

function getReplaysSessionSampleRate(): number {
  // we only have 50 replays in our Sentry plan so we should only use them for errors
  // TODO: Hopefully we can upgrade to a better plan sometime
  // if (environment === "production") return 0.1;
  // if (environment === "staging" || environment === "preview") return 1.0;
  return 0;
}

// Initialize Sentry after router is created (required for tracing integration)
if (import.meta.env.VITE_SENTRY_DSN) {
  const tracesSampleRate = import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE as
    | string
    | undefined;
  const sentryEnvironment =
    (import.meta.env.VITE_SENTRY_ENVIRONMENT as string) || "development";

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN as string,
    environment: sentryEnvironment,
    release: import.meta.env.VITE_SENTRY_RELEASE as string | undefined,
    sendDefaultPii: true,
    // Demo-mode blocks (FOG-2234) are expected, by-design rejections — a
    // demo prospect clicking a blocked action must not page anyone.
    //
    // "getZoomDecimal" is a known, unfixed race inside @grapesjs/studio-sdk's
    // own React wrapper (confirmed present through at least v1.2.1): its
    // editor-init effect assigns the instance only after an async plugin
    // load resolves, but the cleanup closure checks that same
    // still-undefined reference, so `destroy()` becomes a no-op if the
    // component unmounts (e.g. fast navigation away from the exchange
    // editor) before init finishes. The abandoned instance can then throw
    // later from its own scroll/resize-driven selection-tools update. Not
    // triggerable by anything in our own editor lifecycle code — safe to
    // silence until upstream fixes it.
    ignoreErrors: [
      "DemoModeBlockedError",
      "This is a demo — changes are disabled.",
      "Cannot read properties of undefined (reading 'getZoomDecimal')",
    ],
    integrations: [
      Sentry.tanstackRouterBrowserTracingIntegration(router),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: tracesSampleRate ? parseFloat(tracesSampleRate) : 0,
    tracePropagationTargets: [
      "localhost",
      /^https:\/\/.*\.queen\.one/,
      /^https:\/\/.*\.run\.app/,
      /^https:\/\/.*\.encr\.app/,
    ],
    replaysSessionSampleRate: getReplaysSessionSampleRate(),
    replaysOnErrorSampleRate: 1.0,
    // Safety net behind captureDemoFragment(): any URL that still carries a
    // fragment loses it before the event leaves the browser.
    beforeSend: withoutUrlFragment,
    beforeSendTransaction: withoutUrlFragment,
  });
}

const { appReady, markAppReady } = createAppReadySignal();

const rootElement = document.getElementById("root")!;
const bootEl = document.getElementById("boot-loader");
const root = ReactDOM.createRoot(rootElement);

const boot = createBootLoader(bootEl);

// Extract render logic to avoid duplication
function renderApp() {
  root.render(
    <StrictMode>
      <ThemeProvider theme={orionDesignSystemTheme}>
        <CssBaseline />

        <QueryClientProvider client={queryClient}>
          <SnackbarProvider
            maxSnack={3}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            autoHideDuration={6000}
            Components={{
              success: OrionSnackbar,
              error: OrionSnackbar,
              warning: OrionSnackbar,
              info: OrionSnackbar,
              restricted: OrionSnackbar,
            }}
            preventDuplicate={true}
          >
            <AuthProvider>
              <AuthRouterProvider onReady={markAppReady} />
            </AuthProvider>
          </SnackbarProvider>
          {import.meta.env.VITE_ENABLE_DEVTOOLS !== "false" && (
            <ReactQueryDevtools initialIsOpen={false} />
          )}
        </QueryClientProvider>
      </ThemeProvider>
    </StrictMode>,
  );

  void (async () => {
    try {
      await boot.finishWhen(appReady);
    } catch (error) {
      console.error(error);
    }
  })();
}

// Note: enableMocking() handles all errors internally and always resolves,
// so the app will always render whether MSW succeeds or fails
void enableMocking().then(() => {
  renderApp();
});
